import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { Delete, ArrowBack, Edit, MoreVert, ContentCopy, Check, AddCircleOutline } from "@mui/icons-material";
import ServerSideDataTable from "@components/ServerSideDataTable";
import { useSnackbar } from "notistack";
import { urls } from "@routes";
import IconButton from "@mui/material/IconButton";
import { format } from "date-fns";


interface GenerateData {
  id: string
  prefix: string
  name: string
  created: string
  revoked: boolean
}

interface PageData {
  status_code: string
  status_text: string

  user_id: number
  data: GenerateData[]
}

interface ApiSendingState {
  api_sending_disabled: boolean
  api_send_interval_seconds: number | null
  platform_send_interval_seconds: number
  review_status: string | null
  suspicious_count: number
  scheduled_count: number
}

function ApiSendingControls({ userId }: { userId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [intervalOverride, setIntervalOverride] = useState<string>("");

  const stateQuery = useQuery({
    queryKey: ["adminUserApiSendingState", userId],
    queryFn: () => authenticateAndFetchData(`/admin/api-abuse/user-state/?user_id=${userId}`),
    retry: retryFn,
    refetchOnWindowFocus: false,
  });
  const state = stateQuery.data?.data as ApiSendingState | undefined;

  useEffect(() => {
    if (state) {
      setIntervalOverride(state.api_send_interval_seconds === null ? "" : String(state.api_send_interval_seconds));
    }
  }, [state]);

  const actionMutation = useMutation({
    mutationFn: (data: { action: string, send_interval_seconds?: number | null }) =>
      authenticateAndPostData("/admin/api-abuse/action/", { user_id: Number(userId), ...data }),
    onSuccess: () => {
      enqueueSnackbar("API sending updated", { variant: "success" });
      stateQuery.refetch().then();
    },
    onError: (error: ApiRequestFailed) => {
      enqueueSnackbar(error.data.message, { variant: "error" });
    },
  });

  if (!state) {
    return null;
  }

  return (
    <Stack direction={"row"} spacing={2} alignItems={"center"} flexWrap={"wrap"}>
      <FormControlLabel
        control={
          <Switch
            checked={!state.api_sending_disabled}
            disabled={actionMutation.isPending}
            onChange={(e) => actionMutation.mutate({ action: e.target.checked ? "resume_api_sending" : "stop_api_sending" })}
          />
        }
        label="API Sending Enabled"
      />
      <TextField
        size="small"
        type="number"
        label="Send interval override (seconds)"
        placeholder={`Platform default: ${state.platform_send_interval_seconds}`}
        helperText={`Empty uses the platform default (${state.platform_send_interval_seconds}s per domain).`}
        value={intervalOverride}
        onChange={(e) => setIntervalOverride(e.target.value)}
      />
      <Button
        variant="outlined"
        disabled={actionMutation.isPending}
        onClick={() => actionMutation.mutate({
          action: "set_send_interval",
          send_interval_seconds: intervalOverride === "" ? null : Number(intervalOverride),
        })}
      >
        Save interval
      </Button>
      <Typography variant="body2" color="text.secondary">
        {state.scheduled_count} queued · {state.suspicious_count} flagged
        {state.review_status ? ` · review: ${state.review_status.replace(/_/g, " ")}` : ""}
      </Typography>
    </Stack>
  );
}


export default function AdminDeliverymanAPIKey() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [pageData, setPageData] = useState<PageData>();
  const [generateAPIKeyData, setGenerateAPIKeyData] = useState<GenerateData[]>([]);
  const [openapikeyDialog, setOpenapikeyDialog] = useState<boolean>(false);
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [revokeOpen, setRevokeOpen] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [renameValue, setRenameValue] = useState<string>("");
  const [allowApiKeyCreation, setAllowApiKeyCreation] = useState<boolean>(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);

  // Query to fetch page data.    
  const pageDataQuery = useQuery({
    queryKey: ["getGenerateAPIKey", userId],
    queryFn: () => authenticateAndFetchData(`/admin/api-key/?user_id=${userId}`),
    gcTime: 0,
    retry: retryFn,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (pageDataQuery.data) {
      let data = pageDataQuery.data
      setPageData(data.data)
      setGenerateAPIKeyData(data.data as GenerateData[]);
    }
  }, [pageDataQuery.data]);

  // Mutation to Rename API Key
  const renameApiKeyMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      authenticateAndPostData(
        "/admin/api-key/",
        {
          api_key_id: id,
          api_key_name: name,
          user_id: Number(userId),
        },
        "patch"
      ),
    onSuccess: () => {
      enqueueSnackbar("Renamed Successfully", { variant: "success" });
      pageDataQuery.refetch();
      setRenameOpen(false);
    },
  });

  // Mutation to Revoke API Key
  const revokeApiKeyMutation = useMutation({
    mutationFn: () =>
      authenticateAndPostData("/admin/revoke-api-key/", {
        api_key_id: selectedKey,
        user_id: Number(userId),
      }),
    onSuccess: () => {
      enqueueSnackbar("Revoked Successfully", { variant: "success" });
      pageDataQuery.refetch();
      setRevokeOpen(false);
    },
  });

  // Mutation to Get API Key Permission
  const getApiKeyPermission = useQuery({
    queryKey: ["apiKeyPermission", userId],
    queryFn: () =>
      authenticateAndFetchData(
        `/admin/user-api-key-creation-allowed/?user_id=${userId}`
      ),
    enabled: !!userId,
  });

  useEffect(() => {
    if (getApiKeyPermission.data) {
      setAllowApiKeyCreation(
        getApiKeyPermission.data.data.allowed as boolean
      );
    }
  }, [getApiKeyPermission.data]);

  // Mutation to Update API Key Permission
  const updateApiKeyPermission = useMutation({
    mutationFn: (allowed: boolean) =>
      authenticateAndPostData(
        "/admin/user-api-key-creation-allowed/",
        {
          user_id: Number(userId),
          allowed: allowed,
        },
        "patch"
      ),
    onSuccess: (res) => {
      setAllowApiKeyCreation(res.data.allowed);
      enqueueSnackbar("Permission updated", { variant: "success" });
    },
    onError: (error: ApiRequestFailed) => {
      console.error(error);
      enqueueSnackbar(error.data.message, { variant: "error" });
    },
  });

  function onApiKeyCreated(keyValue: string) {
    setNewlyCreatedKey(keyValue);
    pageDataQuery.refetch().then();
    setOpenapikeyDialog(false);
  }


  const ActionsMenu = ({ row }: { row: GenerateData }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    return (
      <>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVert />
        </IconButton>

        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>

          <MenuItem
            onClick={() => {
              setSelectedKey(row.id);
              setRenameValue(row.name);
              setRenameOpen(true);
              setAnchorEl(null);
            }}
          >
            <Edit sx={{ mr: 1 }} />
            Rename
          </MenuItem>

          <MenuItem
            disabled={row.revoked}
            onClick={() => {
              setSelectedKey(row.id);
              setRevokeOpen(true);
              setAnchorEl(null);
            }}
          >
            <Delete sx={{ mr: 1 }} />
            Revoke
          </MenuItem>

        </Menu>
      </>
    );
  };

  const CreateAPIKeyButton = () => {
    return (
      <Button startIcon={<AddCircleOutline />}
        variant="contained"
        color="primary"
        disabled={!allowApiKeyCreation}
        onClick={() => {
          setOpenapikeyDialog(true);
        }}>
        Create New API Key
      </Button>
    )
  }

  const APIKeyCell = ({ value }: CustomCellRendererProps) => {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontFamily="monospace">
          {value}{"•".repeat(8)}
        </Typography>
      </Stack>
    );
  };

  // Columns for API lists table.
  const columnDefs: ColDef[] = [
    { field: "name", headerName: "Key Name", },
    { field: "prefix", headerName: "API Key", cellRenderer: APIKeyCell, sortable: false },
    {
      field: "created", headerName: "Created on", valueFormatter: (params: ValueFormatterParams) =>
        format(new Date(params.value), "yyyy-MM-dd"),
    },
    {
      field: "revoked", headerName: "Status", cellRenderer: (params: CustomCellRendererProps) =>
        params.value ? (
          <Chip size="small" color="error" label="Revoked" />
        ) : (
          <Chip size="small" color="success" label="Active" />
        ),
    },
    {
      headerName: "Actions",
      cellRenderer: (props: CustomCellRendererProps) => (
        <ActionsMenu row={props.data} />
      ),
      sortable: false,
    },
  ];


  // ========================================================================
  // --------------------------- MAIN RENDER CODE ---------------------------
  // ========================================================================

  if (pageDataQuery.isLoading || pageDataQuery.isRefetching) {
    return (
      <PageLoading />
    )

  } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
    return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />

  } else if (pageData) {
    return (
      <Stack direction={"column"} spacing={4} sx={{ pb: 4, height: "100%" }}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <IconButton
            onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}><ArrowBack /></IconButton>
          <Typography fontWeight={"bold"} variant={"h5"}>Deliveryman API Keys
            <FormControlLabel
              control={
                <Switch
                  checked={allowApiKeyCreation}
                  disabled={updateApiKeyPermission.isPending}
                  onChange={(e) => {
                    updateApiKeyPermission.mutate(e.target.checked);
                  }}
                />
              }
              sx={{ ml: 1 }}
              label="Allow API Key Creation"
            />

          </Typography>
        </Stack>
        <ApiSendingControls userId={userId || ""} />
        <Divider />
        <Box sx={{ height: "100%", maxHeight: 800 }}>
          <ServerSideDataTable columns={columnDefs}
            rows={generateAPIKeyData || []}
            noRowsText={"No API Keys List Available"}
            loading={pageDataQuery.isFetching || pageDataQuery.isLoading}
            actions={CreateAPIKeyButton()}
          />
        </Box>
        <CreateAPIKeyDialog open={openapikeyDialog}
          userId={userId!}
          close={() => setOpenapikeyDialog(false)}
          success={onApiKeyCreated} />

        <NewKeyCreatedDialog
          apiKeyValue={newlyCreatedKey}
          onClose={() => setNewlyCreatedKey(null)}
        />

        <Dialog open={renameOpen} onClose={() => setRenameOpen(false)}>
          <DialogTitle>Rename API Key</DialogTitle>

          <DialogContent>
            <TextField
              fullWidth
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button
              disabled={!allowApiKeyCreation}
              onClick={() =>
                renameApiKeyMutation.mutate({
                  id: selectedKey,
                  name: renameValue,
                })
              }
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)}>
          <DialogTitle>Revoke API Key</DialogTitle>

          <DialogContent>
            <DialogContentText>
              This will disable the API key permanently.
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setRevokeOpen(false)}>Cancel</Button>
            <Button color="error" onClick={() => revokeApiKeyMutation.mutate()}>
              Revoke
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    )

  } else {
    // Ideally it should not reach here.
    return <></>
  }
}


function CreateAPIKeyDialog(props: {
  userId: string,
  open: boolean,
  close: () => void,
  success: (keyValue: string) => void,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [listName, setListName] = useState<string>("");

  const generateApiKeyMutation = useMutation({
    mutationFn: () =>
      authenticateAndPostData("/admin/api-key/", {
        api_key_name: listName.trim(),
        user_id: Number(props.userId),
      }),
    onSuccess: (res) => {
      props.success(res.data.api_key_value);
    },
    onError: () => {
      enqueueSnackbar("Failed to generate API key.", { variant: "error" });
    },
  });

  function confirmButtonHandler() {
    if (!listName.trim()) {
      enqueueSnackbar("API Key name is required.", { variant: "error" });
      return;
    }
    generateApiKeyMutation.mutate();
  }

  return (
    <Dialog open={props.open}>
      <DialogTitle>Create New API Key</DialogTitle>
      <DialogContent>
        <Stack direction={"column"} spacing={1} sx={{ mt: 2 }}>
          <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
            API Key Name:
          </Typography>
          <TextField value={listName}
            onChange={e => setListName(e.target.value)}
            variant="outlined"
            required />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.close} disabled={generateApiKeyMutation.isPending}>Cancel</Button>
        <Button onClick={confirmButtonHandler} disabled={generateApiKeyMutation.isPending}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function NewKeyCreatedDialog(props: {
  apiKeyValue: string | null,
  onClose: () => void,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!props.apiKeyValue) return;
    navigator.clipboard.writeText(props.apiKeyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={props.apiKeyValue !== null} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>API Key Created</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText color="warning.main">
            Copy this key now — it will not be shown again. Store it somewhere safe.
          </DialogContentText>
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{ fontFamily: "monospace", bgcolor: "action.hover", borderRadius: 1, px: 2, py: 1, wordBreak: "break-all" }}>
            <Typography fontFamily="monospace" sx={{ flex: 1 }}>
              {props.apiKeyValue}
            </Typography>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={props.onClose}>
          I've saved my key
        </Button>
      </DialogActions>
    </Dialog>
  )
}